/**
 * Options for a `Select` that searches on the server (long lists such as vehicle makes): nothing loads until the list
 * is opened, then only the first page of matches for what was typed.
 *
 *     const makes = useRemoteOptions({
 *       queryKey: ["vehicles", "make-options"],
 *       fetch: async (search) => (await adminVehiclesMakesList({ search, page_size: 50 })).data.results,
 *       toOption: (make) => ({ value: make.uid, label: make.name }),
 *     });
 *     <Select {...makes.selectProps} value={make} onValueChange={setMake} selectedLabel={makes.labelOf(make)} />
 */

import { keepPreviousData, useQuery, type QueryKey } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";

import type { SelectOption } from "@motolynk/ui";

export interface RemoteOptionsConfig<Row> {
  queryKey: QueryKey;
  /** Loads the rows matching the search text ("" = the first page). */
  fetch: (search: string) => Promise<Row[]>;
  toOption: (row: Row) => SelectOption;
  /** False keeps it from loading (e.g. models before a make is chosen). */
  enabled?: boolean;
}

export function useRemoteOptions<Row>({
  queryKey,
  fetch,
  toOption,
  enabled = true,
}: RemoteOptionsConfig<Row>) {
  // null until the list is first opened: closed selects cost no request
  const [search, setSearch] = useState<string | null>(null);
  // Labels of every option loaded, so a chosen value keeps its name when a later search doesn't include it.
  const seen = useRef(new Map<string, string>());
  const query = useQuery({
    queryKey: [...queryKey, { search }],
    queryFn: async () => {
      const rows = await fetch(search ?? "");
      for (const row of rows) {
        const option = toOption(row);
        seen.current.set(option.value, option.label);
      }
      return rows;
    },
    enabled: enabled && search !== null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const options = (query.data ?? []).map(toOption);
  const labelOf = useCallback((value: string | null | undefined) => {
    return value ? seen.current.get(value) : undefined;
  }, []);
  return {
    /** Spread into `<Select>`. */
    selectProps: {
      options,
      onSearch: setSearch,
      loading: search !== null && query.isPending,
    },
    labelOf,
  };
}
